import { toaster } from '@/components/ui/toaster';
import axiosInstance from '@/libs/axiosInstance';
import type { CodePattern } from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface MigrateDiscountCodesVariables {
  programId: string;
  codePattern: CodePattern;
}

interface MigrateDiscountCodesResponse {
  success: boolean;
  code: string;
  message: string;
  data: { migrationId: string };
}

/**
 * The backend names the refusal (a migration already running answers 409 with
 * `E_DISCOUNT_CODE_MIGRATION_RUNNING`), and the dashboard shows that English
 * message as-is. Rethrowing it as the error's own message keeps the reason
 * available to the caller as well as to the toaster.
 */
const messageOf = (error: unknown): string | null => {
  if (isAxiosError(error)) {
    const message = (error.response?.data as { message?: string } | undefined)?.message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return null;
};

export const useMigrateProgramDiscountCodes = () => {
  const { t } = useTranslation();

  return useMutation<MigrateDiscountCodesResponse, Error, MigrateDiscountCodesVariables>({
    mutationFn: async ({ programId, codePattern }) => {
      try {
        const response = await axiosInstance.post<MigrateDiscountCodesResponse>(
          `/dashboard/programs/${programId}/discount-codes/migrate`,
          { codePattern }
        );
        return response.data;
      } catch (error) {
        const message = messageOf(error);
        throw message ? new Error(message) : (error as Error);
      }
    },
    onError: (error: Error) => {
      console.error('Failed to migrate discount codes:', error);

      toaster.error({
        title: t('common.error'),
        description: error.message,
      });
    },
  });
};
